<script setup lang="ts">
// Maintenance-reminder subscription. Requires a verified identity (same
// email verification as the AI gate); shows the gate inline when needed.
import { computed, ref } from 'vue'
import { NButton, NSelect } from 'naive-ui'
import { useMutation } from '@tanstack/vue-query'
import { postJson } from '@/shared/api/client'
import { useIdentity } from '@/shared/composables/useIdentity'
import EmailVerifyGate from './EmailVerifyGate.vue'

const { me } = useIdentity()
const verified = computed(() => me.data.value?.verified === true)

const cadence = ref<string>('seasonal')
const cadenceOptions = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Seasonal (4×/year)', value: 'seasonal' },
    { label: 'Twice a year', value: 'biannual' },
]

const done = ref(false)
const subscribe = useMutation({
    mutationFn: () =>
        postJson<{ id: string; active: boolean }>('/api/reminders', {
            cadence: cadence.value,
            topics: ['filter_change', 'seasonal_checkup'],
        }),
    onSuccess: () => {
        done.value = true
    },
})
</script>

<template>
    <div class="reminder-signup">
        <h2>Free maintenance reminders</h2>
        <p class="muted">
            A short email when it's time to change your filter or book a seasonal checkup.
            Nothing else — unsubscribe anytime.
        </p>

        <div v-if="done" class="thanks">
            <p><strong>You're set ✓</strong> — first reminder lands next season.</p>
        </div>
        <template v-else-if="verified">
            <div class="row">
                <NSelect v-model:value="cadence" :options="cadenceOptions" style="max-width: 220px" />
                <NButton
                    type="primary"
                    :loading="subscribe.isPending.value"
                    @click="subscribe.mutate()"
                >
                    Remind Me
                </NButton>
            </div>
            <p v-if="subscribe.isError.value" class="error">
                {{ (subscribe.error.value as Error)?.message ?? 'Something went wrong.' }}
            </p>
        </template>
        <EmailVerifyGate v-else />
    </div>
</template>

<style scoped lang="scss">
.reminder-signup {
    .row {
        display: flex;
        gap: var(--site-space-sm);
        align-items: center;
    }

    .error {
        color: var(--site-danger);
        margin-top: var(--site-space-sm);
    }
}
</style>
